/**
 * The company side panel's state, held in the URL: `?company=<id>` on a
 * company list. Same history rules as the person panel (`usePersonPanelUrl`,
 * whose docstring has the reasons):
 *
 * - **open** PUSHES and marks the entry, so Back closes the panel;
 * - **move** (‹ ›, ←/→) REPLACES, so walking 50 companies adds no history;
 * - **close** goes BACK after an open from this page, and REPLACES after a
 *   deep link (nothing below it to go back to).
 *
 * Only `company` is written; any other param, and every other history-state
 * key (`from`, for the Back link), survives.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const PARAM = "company";
const PUSHED_KEY = "companyPanelPushed";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function asRecord(state: unknown): Record<string, unknown> {
  return typeof state === "object" && state !== null ? (state as Record<string, unknown>) : {};
}

export interface CompanyPanelUrl {
  /** The open company's id, or null (closed, or `company=` is not an id). */
  companyId: string | null;
  open: (companyId: string) => void;
  move: (companyId: string) => void;
  close: () => void;
}

export function useCompanyPanelUrl(): CompanyPanelUrl {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const raw = searchParams.get(PARAM);
  const companyId = raw && UUID.test(raw) ? raw : null;
  const pushedHere = asRecord(location.state)[PUSHED_KEY] === true;

  const write = useCallback(
    (value: string | null, replace: boolean, state: unknown) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(PARAM);
          else next.set(PARAM, value);
          return next;
        },
        { replace, state }
      ),
    [setSearchParams]
  );

  const open = useCallback(
    (id: string) => write(id, false, { ...asRecord(location.state), [PUSHED_KEY]: true }),
    [write, location.state]
  );
  const move = useCallback(
    (id: string) => write(id, true, location.state ?? null),
    [write, location.state]
  );
  const close = useCallback(() => {
    if (pushedHere) {
      navigate(-1);
      return;
    }
    const { [PUSHED_KEY]: _mark, ...rest } = asRecord(location.state);
    write(null, true, Object.keys(rest).length > 0 ? rest : null);
  }, [pushedHere, navigate, write, location.state]);

  return useMemo(() => ({ companyId, open, move, close }), [companyId, open, move, close]);
}
